import { EventHealthApiIssue, EventHealthApiResponse } from '../../tools-reports/event-health/event-health.types';
import { mapEventHealthGuidance } from './event-health-guidance.mapper';
import { HealthGuidanceOptions, normalizeHealthScope, normalizeHealthSeverity } from './event-health-targets';
import {
  HealthReviewAssessment,
  HealthReviewContext,
  HealthReviewFinding,
  HealthReviewStatus,
} from './event-health-form-review.types';

const PROFILE = '1.2' as const;

export function mapHealthReview(response: EventHealthApiResponse, returnTo: string, options: HealthGuidanceOptions = {}): {
  context: HealthReviewContext;
  assessment: HealthReviewAssessment;
} {
  const profileAssessment = response.assessments?.[PROFILE];
  const issueFindings = (profileAssessment?.issues ?? []).map((issue, index) => mapIssue(response.id, response.eventType, issue, index, options));
  const missingEvidenceFindings = (profileAssessment?.missingEvidence ?? []).map((path, index) =>
    mapMissingEvidence(response.id, response.eventType, path, issueFindings.length + index, options),
  );

  return {
    context: {
      eventUid: response.id,
      eventType: response.eventType,
      profile: PROFILE,
      status: normalizeStatus(profileAssessment?.status),
      returnTo,
      reason: profileAssessment?.reason,
      requiredNotAssessed: profileAssessment?.requiredNotAssessed ?? 0,
    },
    assessment: {
      assessmentId: `${response.id}-${PROFILE}-${response.assessedAt ?? 'stored'}`,
      findings: [...issueFindings, ...missingEvidenceFindings],
    },
  };
}

function mapIssue(eventUid: string, eventType: string, issue: EventHealthApiIssue, index: number, options: HealthGuidanceOptions): HealthReviewFinding {
  const guidance = mapEventHealthGuidance(eventType, issue, options);

  return {
    id: `${eventUid}-${PROFILE}-issue-${index + 1}`,
    ruleId: issue.code,
    severity: normalizeHealthSeverity(issue.severity),
    scope: normalizeHealthScope(issue.category),
    target: guidance.target,
    title: guidance.title || issue.title?.trim() || 'Event information needs attention',
    message: issue.description,
    label: issue.label,
    reason: issue.reason,
    requiredCertificateType: issue.requiredCertificateType,
    missingPaths: issue.missingPaths ? [...issue.missingPaths] : undefined,
    entityType: issue.entityType,
    reference: issue.reference,
    help: guidance.help,
    suggestions: [],
  };
}

function mapMissingEvidence(eventUid: string, eventType: string, path: string, index: number, options: HealthGuidanceOptions): HealthReviewFinding {
  const controlName = normalizeField(path);
  const guidance = mapEventHealthGuidance(eventType, {
    code: 'CLASSIFICATION-INCOMPLETE', severity: 'error', category: 'event-data',
    field: controlName, path, title: 'Required evidence is missing', description: '',
  }, options);

  return {
    id: `${eventUid}-${PROFILE}-evidence-${index + 1}`,
    ruleId: 'CLASSIFICATION-INCOMPLETE',
    severity: 'error',
    scope: 'event-data',
    target: guidance.target,
    title: `${humanize(controlName || 'Required evidence')} is missing`,
    message: 'The validator needs this information to identify the event type.',
    help: guidance.help,
    suggestions: [],
  };
}

function normalizeField(field?: string): string {
  if (!field) return '';
  const segments = field.replace(/^\/+/, '').split('/').filter(Boolean);
  return segments.at(-1)?.replace(/~1/g, '/').replace(/~0/g, '~') ?? '';
}

function humanize(value: string): string {
  return value
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/[_-]+/g, ' ')
    .replace(/^./, (letter) => letter.toUpperCase());
}

function normalizeStatus(value?: string | null): HealthReviewStatus {
  if (value === 'ready' || value === 'needs-action' || value === 'partially-assessed' || value === 'not-assessed') return value;
  return 'needs-review';
}

/** Explain the requirement without inventing an offending value or repeating recovery instructions. */
export function healthFindingExplanation(finding: HealthReviewFinding): string {
  switch (finding.reason) {
    case 'master-data-unavailable':
      return 'The linked record could not be checked. This does not confirm that its information is incorrect.';
    case 'master-data-not-found':
      return 'The event refers to a linked record that the validator could not find.';
    case 'master-data-field-missing':
      return 'The linked record was found, but it does not contain information required by this check.';
    case 'missing-matching-entry':
      return 'The validator could not find a certificate matching the type required for this event.';
    case 'incomplete-entry':
      return 'A matching certificate is present, but required certificate information is incomplete.';
  }
  if (finding.scope === 'master-data') {
    return 'Information in a linked record does not meet this validation rule. Editing the event alone may not resolve the finding.';
  }
  switch (finding.target.field) {
    case 'catchArea':
      return 'The validator could not confirm the required catch-area information, which identifies where the fish were caught.';
    case 'fishingGearTypeCode':
      return 'The validator could not confirm an accepted fishing-gear classification, which identifies the capture method.';
    case 'uom':
      return 'The validator could not confirm an accepted unit of measure needed to interpret this item’s quantity.';
    case 'epcClass':
      return 'The item’s product identifier does not meet the identification requirements of this check.';
    case 'quantity':
      return 'The item’s quantity does not meet the requirements of this check.';
    case 'certification':
      return 'The certificate information does not meet this event’s certification requirements.';
    default:
      return 'The validator flagged information that does not meet this rule’s requirements.' +
        (finding.target.path ? ' The data path below identifies the affected part of the event.' : '');
  }
}

/** A missing finding is not proof that all rules ran successfully. */
export function healthAssessmentNotice(context: HealthReviewContext | null): string {
  if (!context) return '';
  if (context.reason === 'schema-invalid') {
    return 'Fix the event data shown below, then save and check its health again. Event-specific checks have not run yet.';
  }
  if (context.reason === 'classification-incomplete') {
    return 'The validator needs more information to identify this event. Complete the findings below, then save and check its health again.';
  }
  if (context.requiredNotAssessed) {
    return `${context.requiredNotAssessed} required checks could not be completed. Save and check event health when the required data is available.`;
  }
  if (context.status === 'partially-assessed') return 'Some checks could not be completed. This event has not been fully assessed.';
  if (context.status === 'not-assessed') return 'This event has not been assessed. Save the event and check its health again.';
  return '';
}
